import React from 'react';
import { Form } from 'react-final-form';
import { ApolloError } from '@apollo/client';
import arrayMutators from 'final-form-arrays';
import { useNavigate } from 'react-router-dom';

import { Page, Button } from '@occc-app/design-system';
import { clearFormField as clearFormFieldMutator } from '@occc-app/lib/finalFormUtils';

import { AudienceUpdateFormRenderComponent } from './AudienceUpdateFormRenderComponent';

import {
  AudienceFormInitialValues,
  AudienceFormProps,
  AudienceFormRenderProps,
  UpdateAudiencePageProps,
  AudienceFormValues,
} from '../types';

interface IAudienceUpdatePageProps extends UpdateAudiencePageProps {
  loading: boolean;
  loaded: boolean;
  audienceId: string;
  error?: ApolloError;
  isUserPermittedToEditAudience: boolean;
}

export const AudienceUpdatePage: React.FC<IAudienceUpdatePageProps> = ({
  initialValues,
  validate,
  audienceId,
  audienceUpdate,
  loading,
  loaded,
  error,
  openAndScrollToInvalidSection,
  isUserPermittedToEditAudience,
}) => {
  const navigate = useNavigate();
  const renderForm: AudienceFormProps['render'] = React.useCallback(
    ({ form, handleSubmit, values, submitting }: AudienceFormRenderProps) => {
      return (
        <Page
          browserTabTitle='Audience Update'
          containerProps={{ variant: 'md' }}
          pageHeaderProps={{ title: 'Audience Update' }}
          actions={
            <>
              <Button
                variant='outlined'
                color='secondary'
                onClick={() => navigate(-1)}
                data-testid='campaign-cancel-button'
                role='button'
              >
                Cancel
              </Button>
              {isUserPermittedToEditAudience && (
                <Button
                  variant='contained'
                  color='secondary'
                  onClick={() => {
                    openAndScrollToInvalidSection(form);
                    handleSubmit();
                  }}
                  disabled={submitting}
                  sx={{
                    ml: 1,
                  }}
                >
                  Save
                </Button>
              )}
            </>
          }
        >
          <AudienceUpdateFormRenderComponent
            handleSubmit={handleSubmit}
            name={values.name}
            audienceId={audienceId}
            clearFormField={form.mutators.clearFormField}
            loading={loading}
            loaded={loaded}
            error={error}
            isUserPermittedToEditAudience={isUserPermittedToEditAudience}
          />
        </Page>
      );
    },
    [
      navigate,
      audienceId,
      loading,
      loaded,
      error,
      openAndScrollToInvalidSection,
      isUserPermittedToEditAudience,
    ],
  );

  return (
    <Form<AudienceFormValues, AudienceFormInitialValues>
      initialValues={initialValues}
      keepDirtyOnReinitialize={true}
      onSubmit={audienceUpdate}
      render={renderForm}
      validate={validate}
      mutators={{ ...arrayMutators, clearFormField: clearFormFieldMutator }}
    />
  );
};
